import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, Globe, Mail } from "lucide-react";

interface ScheduleConfigPanelProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  scheduleType: string;
  onScheduleTypeChange: (type: string) => void;
  scheduleTime: string;
  onScheduleTimeChange: (time: string) => void;
  scheduleDays: number[];
  onScheduleDaysChange: (days: number[]) => void;
  scheduleDayOfMonth: number;
  onScheduleDayOfMonthChange: (day: number) => void;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  emailSubject: string;
  onEmailSubjectChange: (subject: string) => void;
  emailTemplate: string;
  onEmailTemplateChange: (template: string) => void;
}

const TIMEZONES = [
  { value: 'Europe/Athens', label: 'Athens (GMT+2/+3)' },
  { value: 'Europe/London', label: 'London (GMT+0/+1)' },
  { value: 'America/New_York', label: 'New York (GMT-5/-4)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8/-7)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+10/+11)' },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

const DAYS_OF_WEEK = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '0', label: 'Sun' },
];

export function ScheduleConfigPanel({
  enabled,
  onEnabledChange,
  scheduleType,
  onScheduleTypeChange,
  scheduleTime,
  onScheduleTimeChange,
  scheduleDays,
  onScheduleDaysChange,
  scheduleDayOfMonth,
  onScheduleDayOfMonthChange,
  timezone,
  onTimezoneChange,
  emailSubject,
  onEmailSubjectChange,
  emailTemplate,
  onEmailTemplateChange,
}: ScheduleConfigPanelProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border rounded-lg p-4">
        <div className="space-y-0.5">
          <Label className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Enable Scheduled Sending
          </Label>
          <p className="text-sm text-muted-foreground">
            Automatically send this report on a schedule
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={onEnabledChange} />
      </div>

      {enabled && (
        <>
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select value={scheduleType} onValueChange={onScheduleTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scheduleType === 'weekly' && (
            <div className="space-y-2">
              <Label>Days of Week</Label>
              <ToggleGroup
                type="multiple"
                value={scheduleDays.map(String)}
                onValueChange={(values) => onScheduleDaysChange(values.map(Number))}
                className="justify-start flex-wrap"
              >
                {DAYS_OF_WEEK.map((day) => (
                  <ToggleGroupItem
                    key={day.value}
                    value={day.value}
                    aria-label={day.label}
                    className="px-3"
                  >
                    {day.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          )}

          {scheduleType === 'monthly' && (
            <div className="space-y-2">
              <Label>Day of Month</Label>
              <Select
                value={String(scheduleDayOfMonth)}
                onValueChange={(v) => onScheduleDayOfMonthChange(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Send Time</Label>
              <Select value={scheduleTime} onValueChange={onScheduleTimeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map((time) => (
                    <SelectItem key={time.value} value={time.value}>
                      {time.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Timezone
              </Label>
              <Select value={timezone} onValueChange={onTimezoneChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Configuration
            </h4>

            <div className="space-y-2">
              <Label>Email Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => onEmailSubjectChange(e.target.value)}
                placeholder="Your {report_name} is ready - {date}"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Template</Label>
              <Textarea
                value={emailTemplate}
                onChange={(e) => onEmailTemplateChange(e.target.value)}
                placeholder="Hello {recipient_name},&#10;&#10;Your scheduled report is ready.&#10;&#10;Click here to view: {share_link}&#10;&#10;Best regards"
                rows={5}
              />
            </div>

            <Alert>
              <AlertDescription className="text-xs">
                Available variables: {'{recipient_name}'}, {'{brand_name}'}, {'{report_name}'}, {'{date}'}, {'{share_link}'}
              </AlertDescription>
            </Alert>
          </div>
        </>
      )}
    </div>
  );
}
